import { useEffect, useLayoutEffect, useRef } from 'react';
import { PendingInteraction, useTableInstrumentationMaybe } from './context';
import { useDataProvider } from '../DataProviders';

/**
 * How it works:
 * 1. Event handlers call `markInteraction(type)` before triggering state changes,
 *    which stores `{ type, startTime: performance.now() }` in a ref.
 * 2. `useLayoutEffect` (runs every render) consumes the pending interaction
 *    and promotes it to an "active" interaction being tracked.
 * 3. When the active interaction is ready (not loading), it schedules a rAF
 *    to measure through the browser's layout + paint, then emits `table.interaction`.
 * 4. The first loading → not-loading transition with no active interaction emits
 *    `table.render` (initial load only).
 */
export const useTrackRenderTiming = (): void => {
  const instrumentation = useTableInstrumentationMaybe();
  const dataProvider = useDataProvider();
  const wasLoadingRef = useRef(false);
  const activeInteractionRef = useRef<PendingInteraction | null>(null);
  const mountTimeRef = useRef<number | null>(performance.now());
  const trackedErrorRef = useRef<unknown>(null);

  // Measure interaction or render timing — runs every render (cost: two ref reads + boolean check)
  useLayoutEffect(() => {
    if (!instrumentation) {
      return;
    }

    const isLoading = dataProvider.isLoading;
    const wasLoading = wasLoadingRef.current;

    // Consume any pending interaction → promote to active
    const pending = instrumentation.consumePendingInteraction();
    if (pending) {
      activeInteractionRef.current = pending;
    }

    const active = activeInteractionRef.current;

    if (active && !isLoading) {
      // Interaction complete — measure through paint
      const { type, startTime, context } = active;
      activeInteractionRef.current = null;
      requestAnimationFrame(() => {
        const duration = performance.now() - startTime;
        instrumentation.trackTiming('table.interaction', duration, {
          interaction_type: type,
          ...context,
        });
      });
    } else if (
      !active &&
      wasLoading &&
      !isLoading &&
      mountTimeRef.current !== null
    ) {
      // Initial load: mount → first data arrival → paint
      const startTime = mountTimeRef.current;
      mountTimeRef.current = null;
      requestAnimationFrame(() => {
        const duration = performance.now() - startTime;
        instrumentation.trackTiming('table.render', duration);
      });
    }
    // If active && isLoading → keep waiting for loading to finish

    wasLoadingRef.current = isLoading;
  });

  // Track errors (deduplicated)
  useEffect(() => {
    if (!instrumentation || !dataProvider.error) {
      return;
    }

    // Skip if same error already tracked
    if (dataProvider.error === trackedErrorRef.current) {
      return;
    }

    trackedErrorRef.current = dataProvider.error;
    instrumentation.trackError(dataProvider.error, { source: 'data_fetch' });
  }, [instrumentation, dataProvider.error]);
};
